import type { ReactNode } from 'react';

import clsx from 'clsx';

import { formatBytes } from '../../lib/formatBytes';
import type { GroupBarSegment } from '../../lib/sidebar/buildBreakdownData';
import Typography from '~/shared/components/Typography';

interface GroupBarRowProps {
  name: string;
  totalSize: number;
  totalCount?: number;
  icon?: ReactNode;
  lengthRatio?: number;
  segments?: GroupBarSegment[];
  signalPct?: number;
  signalColor?: string;
  showSizeBar?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

export default function GroupBarRow({
  name,
  totalSize,
  totalCount,
  icon,
  lengthRatio = 1,
  segments,
  signalPct = 0,
  signalColor = 'var(--color-gray-300)',
  showSizeBar = true,
  clickable = true,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
}: GroupBarRowProps) {
  const formattedSize = formatBytes(totalSize);
  const formattedCount = totalCount?.toLocaleString();

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={
        clickable
          ? formattedCount != null
            ? `${name}, ${formattedSize}, ${formattedCount} volumes`
            : `${name}, ${formattedSize}`
          : undefined
      }
      className={clsx(
        'group flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-transparent text-left w-full min-w-0',
        clickable &&
          'cursor-pointer hover:bg-primary-20 transition-colors duration-[120ms] focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-1'
      )}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <Typography
          variant="caption"
          fontWeight={500}
          color="text-primary-900"
          className="truncate min-w-0 flex-1"
          title={name}
        >
          {name}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={500}
          color="text-gray-700"
          className="tabular-nums shrink-0"
        >
          {formattedSize}
        </Typography>
        {formattedCount != null && (
          <Typography
            variant="caption"
            color="text-gray-700"
            className="tabular-nums shrink-0 min-w-12 text-right"
          >
            ({formattedCount})
          </Typography>
        )}
      </div>
      {showSizeBar && (
        <div
          className="relative h-3 rounded border border-gray-300 overflow-hidden"
          style={{ width: `${Math.max(4, lengthRatio * 100)}%` }}
        >
          {segments?.length ? (
            <div className="flex h-full w-full">
              {segments.map((segment) => (
                <div
                  key={segment.key}
                  className="h-full"
                  style={{
                    width: `${segment.pct}%`,
                    background: segment.color,
                  }}
                />
              ))}
            </div>
          ) : (
            <div
              className="absolute inset-y-0 left-0"
              style={{
                width: `${Math.min(100, signalPct)}%`,
                background: signalColor,
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
